import { ProductPlan, DailyQuota } from 'modules/Interfaces/Company/Company';

export interface PlanRouteOption {
  targetPlan: string;
  targetCycle: 'monthly' | 'annual';
}

export interface BusinessPlanFromAPI {
  _id?: string;
  key: string;
  label: string;
  tagline: string;
  monthlyPriceInPence: number;
  annualMonthlyPriceInPence: number;
  pricing: Record<string, { monthlyPrice: number; annualMonthlyPrice: number }>;
  noOfUsers: number;
  licenseTypes?: string[];
  modules: string[];
  highlights: Array<{ module: string; features: string[] }>;
  upgradeRoutes?: { monthly: PlanRouteOption[]; annual: PlanRouteOption[] };
  downgradeRoutes?: { monthly: PlanRouteOption[]; annual: PlanRouteOption[] };
  quotas?: PlanQuotas;
}

export interface CurrencyInfo {
  code: string;
  symbol: string;
  label: string;
}

export interface PlanQuotas {
  noOfUsers: number;
  whatsAppPerDay: number;
  whatsAppTotalCredit: number;
  whatsAppTemplatePerDay: number;
  whatsAppTemplateTotalCredit: number;
  whatsAppBusinessPerDay: number;
  whatsAppBusinessTotalCredit: number;
  whatsAppBusinessTemplatePerDay: number;
  whatsAppBusinessTemplateTotalCredit: number;
  gsmTextPerDay: number;
  gsmTextTotalCredit: number;
  gsmTextTemplatePerDay: number;
  gsmTextTemplateTotalCredit: number;
  telegramPerDay: number;
  telegramTotalCredit: number;
  telegramTemplatePerDay: number;
  telegramTemplateTotalCredit: number;
  emailPerDay: number;
  emailTotalCredit: number;
  emailTemplatePerDay: number;
  emailTemplateTotalCredit: number;
}

export interface ConfigRow {
  icon: string;
  label: string;
  value: string;
}

export const TOP_TIER_KEY = 'enterprise';
export const DOWNGRADE_PENALTY_RATE = 0.20;

export const COUNTRY_TO_CURRENCY: Record<string, string> = {
  GB: 'GBP', US: 'USD', IN: 'INR', AU: 'AUD', AE: 'AED',
  AT: 'EUR', BE: 'EUR', CY: 'EUR', EE: 'EUR', FI: 'EUR',
  FR: 'EUR', DE: 'EUR', GR: 'EUR', IE: 'EUR', IT: 'EUR',
  LV: 'EUR', LT: 'EUR', LU: 'EUR', MT: 'EUR', NL: 'EUR',
  PT: 'EUR', SK: 'EUR', SI: 'EUR', ES: 'EUR', HR: 'EUR',
};

export const LICENSE_TO_PLAN_KEY: Record<string, string> = {
  PublicFree: 'starter',
  VerifiedPro: 'growth',
  Business: 'pro',
  BusinessPlus: 'enterprise',
  Enterprise: 'enterprise',
};

const defaultQuotas = (): PlanQuotas => ({
  noOfUsers: 0,
  whatsAppPerDay: 0, whatsAppTotalCredit: 0,
  whatsAppTemplatePerDay: 0, whatsAppTemplateTotalCredit: 0,
  whatsAppBusinessPerDay: 0, whatsAppBusinessTotalCredit: 0,
  whatsAppBusinessTemplatePerDay: 0, whatsAppBusinessTemplateTotalCredit: 0,
  gsmTextPerDay: 0, gsmTextTotalCredit: 0,
  gsmTextTemplatePerDay: 0, gsmTextTemplateTotalCredit: 0,
  telegramPerDay: 0, telegramTotalCredit: 0,
  telegramTemplatePerDay: 0, telegramTemplateTotalCredit: 0,
  emailPerDay: 0, emailTotalCredit: 0,
  emailTemplatePerDay: 0, emailTemplateTotalCredit: 0,
});

export const PLAN_QUOTAS: Record<string, PlanQuotas> = {
  starter: {
    ...defaultQuotas(), noOfUsers: 1,
    whatsAppPerDay: 300, whatsAppTemplatePerDay: 10,
    emailPerDay: 500, emailTemplatePerDay: 10,
  },
  growth: {
    ...defaultQuotas(), noOfUsers: 1,
    whatsAppPerDay: 500, whatsAppTemplatePerDay: 20,
    emailPerDay: 1000, emailTemplatePerDay: 20,
  },
  pro: {
    ...defaultQuotas(), noOfUsers: 2,
    whatsAppPerDay: 1000, whatsAppTemplatePerDay: 30,
    emailPerDay: 2000, emailTemplatePerDay: 30,
  },
  enterprise: {
    ...defaultQuotas(), noOfUsers: 10,
    whatsAppPerDay: 1500, whatsAppTemplatePerDay: 100,
    emailPerDay: 10000, emailTemplatePerDay: 100,
    gsmTextPerDay: 200, gsmTextTemplatePerDay: 100,
  },
};

export const QUOTA_DISPLAY: { key: keyof PlanQuotas; label: string; icon: string }[] = [
  { key: 'noOfUsers', label: 'Max Users', icon: '👥' },
  { key: 'whatsAppPerDay', label: 'WhatsApp Messages / Day', icon: '💬' },
  { key: 'whatsAppTotalCredit', label: 'WhatsApp Total Credit', icon: '💬' },
  { key: 'whatsAppTemplatePerDay', label: 'WhatsApp Templates / Day', icon: '📋' },
  { key: 'whatsAppTemplateTotalCredit', label: 'WhatsApp Template Total Credit', icon: '📋' },
  { key: 'whatsAppBusinessPerDay', label: 'WA Business Messages / Day', icon: '💼' },
  { key: 'whatsAppBusinessTotalCredit', label: 'WA Business Total Credit', icon: '💼' },
  { key: 'whatsAppBusinessTemplatePerDay', label: 'WA Business Templates / Day', icon: '📑' },
  { key: 'whatsAppBusinessTemplateTotalCredit', label: 'WA Business Template Total Credit', icon: '📑' },
  { key: 'gsmTextPerDay', label: 'SMS Messages / Day', icon: '📱' },
  { key: 'gsmTextTotalCredit', label: 'SMS Total Credit', icon: '📱' },
  { key: 'gsmTextTemplatePerDay', label: 'SMS Templates / Day', icon: '📄' },
  { key: 'gsmTextTemplateTotalCredit', label: 'SMS Template Total Credit', icon: '📄' },
  { key: 'telegramPerDay', label: 'Telegram Messages / Day', icon: '✈️' },
  { key: 'telegramTotalCredit', label: 'Telegram Total Credit', icon: '✈️' },
  { key: 'telegramTemplatePerDay', label: 'Telegram Templates / Day', icon: '📑' },
  { key: 'telegramTemplateTotalCredit', label: 'Telegram Template Total Credit', icon: '📑' },
  { key: 'emailPerDay', label: 'Emails / Day', icon: '📧' },
  { key: 'emailTotalCredit', label: 'Email Total Credit', icon: '📧' },
  { key: 'emailTemplatePerDay', label: 'Email Templates / Day', icon: '📝' },
  { key: 'emailTemplateTotalCredit', label: 'Email Template Total Credit', icon: '📝' },
];

export const buildConfigRows = (plan: ProductPlan): ConfigRow[] => {
  const rows: ConfigRow[] = [];
  const perDay = (quota: DailyQuota | undefined) => quota?.perDayLimit;
  const credit = (quota: DailyQuota | undefined) => quota?.totalCredit;

  const addQuotaRows = (
    quota: DailyQuota | undefined,
    dayIcon: string, dayLabel: string,
    creditIcon: string, creditLabel: string,
  ) => {
    if (perDay(quota) != null)
      rows.push({ icon: dayIcon, label: dayLabel, value: (perDay(quota)!).toLocaleString() });
    if (credit(quota) != null && credit(quota)! > 0)
      rows.push({ icon: creditIcon, label: creditLabel, value: (credit(quota)!).toLocaleString() });
  };

  addQuotaRows(plan.whatsApp, '💬', 'WhatsApp Messages / Day', '💬', 'WhatsApp Total Credit');
  addQuotaRows(plan.whatsAppTemplate, '📋', 'WhatsApp Templates / Day', '📋', 'WhatsApp Template Total Credit');
  addQuotaRows(plan.whatsAppBusiness, '💼', 'WA Business Messages / Day', '💼', 'WA Business Total Credit');
  addQuotaRows(plan.whatsAppBusinessTemplate, '📑', 'WA Business Templates / Day', '📑', 'WA Business Template Total Credit');
  addQuotaRows(plan.gsmText, '📱', 'SMS Messages / Day', '📱', 'SMS Total Credit');
  addQuotaRows(plan.gsmTextTemplate, '📄', 'SMS Templates / Day', '📄', 'SMS Template Total Credit');
  addQuotaRows(plan.telegram, '✈️', 'Telegram Messages / Day', '✈️', 'Telegram Total Credit');
  addQuotaRows(plan.telegramTemplate, '📑', 'Telegram Templates / Day', '📑', 'Telegram Template Total Credit');
  addQuotaRows(plan.email, '📧', 'Emails / Day', '📧', 'Email Total Credit');
  addQuotaRows(plan.emailTemplate, '📝', 'Email Templates / Day', '📝', 'Email Template Total Credit');

  if (plan.gsmWorkerName)
    rows.push({ icon: '🔧', label: 'GSM Worker', value: plan.gsmWorkerName });
  if (plan.module === 'instantupdate.ai')
    rows.push({ icon: '📨', label: 'Sender Header Name', value: plan.senderHeaderName ?? '640807' });

  return rows;
};

const getQuotaPerDay = (quota: DailyQuota | undefined): number =>
  quota?.perDayLimit ?? 0;

const getQuotaCredit = (quota: DailyQuota | undefined): number =>
  quota?.totalCredit ?? 0;

export const getActivePlan = (productPlans: ProductPlan[]): ProductPlan | null => {
  const now = new Date();
  return productPlans.find(p => {
    const isActive = p.status?.toLowerCase() === 'active';
    const moduleMatch = p.module?.toLowerCase() === 'instantupdate.ai';
    const started = p.startedAt ? new Date(p.startedAt) <= now : true;
    const notEnded = p.endsAt ? new Date(p.endsAt) >= now : true;
    return isActive && moduleMatch && started && notEnded;
  }) || null;
};

export const extractCurrentQuotas = (plan: ProductPlan): PlanQuotas => ({
  noOfUsers: plan.noOfUsers ?? 0,
  whatsAppPerDay: getQuotaPerDay(plan.whatsApp),
  whatsAppTotalCredit: getQuotaCredit(plan.whatsApp),
  whatsAppTemplatePerDay: getQuotaPerDay(plan.whatsAppTemplate),
  whatsAppTemplateTotalCredit: getQuotaCredit(plan.whatsAppTemplate),
  whatsAppBusinessPerDay: getQuotaPerDay(plan.whatsAppBusiness),
  whatsAppBusinessTotalCredit: getQuotaCredit(plan.whatsAppBusiness),
  whatsAppBusinessTemplatePerDay: getQuotaPerDay(plan.whatsAppBusinessTemplate),
  whatsAppBusinessTemplateTotalCredit: getQuotaCredit(plan.whatsAppBusinessTemplate),
  gsmTextPerDay: getQuotaPerDay(plan.gsmText),
  gsmTextTotalCredit: getQuotaCredit(plan.gsmText),
  gsmTextTemplatePerDay: getQuotaPerDay(plan.gsmTextTemplate),
  gsmTextTemplateTotalCredit: getQuotaCredit(plan.gsmTextTemplate),
  telegramPerDay: getQuotaPerDay(plan.telegram),
  telegramTotalCredit: getQuotaCredit(plan.telegram),
  telegramTemplatePerDay: getQuotaPerDay(plan.telegramTemplate),
  telegramTemplateTotalCredit: getQuotaCredit(plan.telegramTemplate),
  emailPerDay: getQuotaPerDay(plan.email),
  emailTotalCredit: getQuotaCredit(plan.email),
  emailTemplatePerDay: getQuotaPerDay(plan.emailTemplate),
  emailTemplateTotalCredit: getQuotaCredit(plan.emailTemplate),
});

export const isAnnualCommitment = (transactions: any[]): boolean => {
  if (!transactions || transactions.length === 0) return false;
  const sorted = [...transactions].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  return sorted[0]?.planDuration?.includes('12 months') ?? false;
};

export const formatPrice = (amountInMinorUnits: number, symbol: string): string => {
  if (amountInMinorUnits === 0) return 'Free';
  return `${symbol}${(amountInMinorUnits / 100).toFixed(2)}`;
};

export const formatDate = (dateStr: string): string => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};
